import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  ConciergeContainer,
  getAppConsistentParams,
  getProductUrl,
  signOut,
} from "@brandclub/common-ui";
import { useAppSelector } from "@/redux/hooks";
import { useCheckoutDrawer } from "@/components/Checkout/useCheckoutDrawer";
import { getCartCounts } from "@/redux/selectors";
import BagDrawer from "@/components/Checkout/BagDrawer";
import { useNavigate } from "react-router-dom";
import { useCallback, useMemo } from "react";
import "./concierge-tailwind.css";

const queryClient = new QueryClient();

const ConciergeContainerWithIntegration: React.FC = () => {
  const navigate = useNavigate();
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const cartCount = useAppSelector(getCartCounts);
  const { isOpen: isCheckoutDrawerOpen, openDrawer } = useCheckoutDrawer();

  // Convert Redux user profile to common-ui's expected format
  const sidebarUserProfile = useMemo(
    () => ({
      firstName: userProfile?.given_name,
      lastName: userProfile?.family_name,
      isSignedIn: !!userProfile?.userId,
    }),
    [userProfile]
  );

  // Handle variant selection - navigate to the variant's PDP
  const handleVariantSelect = useCallback(
    (stacklineSku: string) => {
      const urlSearchParams = getAppConsistentParams(window.location.search);
      const targetUrl = getProductUrl({
        title: "",
        stacklineSku,
        urlSearchParams,
      });

      navigate({
        pathname: targetUrl.split("?")[0],
        search: targetUrl.split("?")[1],
      });
    },
    [navigate]
  );

  // Handle home click - navigate to the home page
  const handleHomeClick = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const handleSignOut = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("conversationId");
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, "", newUrl);
    await signOut();
  }, []);

  return (
    <ConciergeContainer
      layout="sidebar"
      isEmbedded={false} // Full page should show image carousel, not text suggestions
      userProfile={sidebarUserProfile}
      userFirstName={userProfile?.given_name}
      cartCount={cartCount}
      onBagClick={openDrawer}
      isCheckoutDrawerOpen={isCheckoutDrawerOpen}
      CheckoutDrawerContent={BagDrawer}
      onVariantSelect={handleVariantSelect}
      onHomeClick={handleHomeClick}
      onSignOutClick={handleSignOut}
    />
  );
};

export const ConciergeWithReactQuery: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ConciergeContainerWithIntegration />
    </QueryClientProvider>
  );
};

const Concierge = () => {
  return (
    <div style={{ width: "100%" }}>
      <ConciergeWithReactQuery />
    </div>
  );
};

export default Concierge;
